import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { getFirstFocusableChild as _getFirstFocusableChild } from '../../core/utils/focus-management/get-first-focusable-child.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { tabPanelCSS, tabPanelOverflowBehaviorCSS } from './TabPanel.sty.js';
interface TabPanelProps
  extends WithChildren, DOMProps, AriaLabelingProps, StylingProps, DataTestId {
  /**
   * Whether the tab panel is disabled.
   * If true, its content is not rendered.
   */
  disabled: boolean;
  /** The overflow behavior of the tab panel. */
  panelOverflow: 'hidden' | 'scroll-y';
}

/**
 * TabPanel component, used to display the content of a tab.
 */
export const TabPanel = (props: TabPanelProps) => {
  const {
    children,
    disabled,
    panelOverflow,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...restProps
  } = props;
  const ref = useRef<HTMLDivElement>(null);

  const { focusProps, focusClassName } = useFocusRing({ disabled });
  const [hasFocusableChildren, setHasFocusableChildren] =
    useState<boolean>(false);

  // Check if the tab panel has focusable children to focus on them instead of the panel per a11y rules
  useLayoutEffect(() => {
    if (ref.current) {
      setHasFocusableChildren(_getFirstFocusableChild(ref.current) !== null);
    }
  }, [children]);

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        consumerClassName,
        focusClassName,
        tabPanelCSS,
        tabPanelOverflowBehaviorCSS[panelOverflow],
      )}
      style={consumerStyle}
      ref={ref}
      {..._mergeProps(focusProps, restProps)}
      tabIndex={hasFocusableChildren ? -1 : 0}
      role="tabpanel"
    >
      {!disabled && children}
    </div>
  );
};
TabPanel['displayName'] = 'TabPanel';
